import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { settings } from "@/settings";
import { SWRApiError, swrConfigOptions, useFetcher, useMutater } from "./swr";

type ScanLotStatus = {
  isEnqueued: boolean;
  isRunning: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  runtimeStatus: string | null;
};

type RawScanLotStatus = {
  is_running: boolean;
  runtime_status: string | null;
};

/**
 * Given a measurement request ID, returns the current Pholis scan status and
 * a function to trigger a scan for it. The scan status is polled at a
 * regular interval.
 *
 * @param measurementRequestId - The ID of the measurement request to scan.
 * @returns An object containing the current scan status, a function to trigger a scan, and a function to refetch the status.
 */
export const usePholisScan = (measurementRequestId: number) => {
  const fetcher = useFetcher();
  const mutater = useMutater();

  const {
    data: rawStatus,
    isLoading: isLoadingStatus,
    mutate: refetchStatus,
  } = useSWR<RawScanLotStatus, SWRApiError>(
    { url: `/pholis/scan/${measurementRequestId}/status` },
    fetcher,
    {
      ...swrConfigOptions,
      refreshInterval: settings.pholisPollIntervalMs,
      // clear cache on error
      onError: (_err, key) => mutate(key, undefined, { revalidate: false }),
    },
  );

  const status: ScanLotStatus | null = rawStatus
    ? {
        isEnqueued: rawStatus.runtime_status === "ENQUEUED",
        isRunning: rawStatus.is_running,
        isCompleted: rawStatus.runtime_status === "COMPLETED",
        isFailed: rawStatus.runtime_status === "FAILED",
        runtimeStatus: rawStatus.runtime_status ?? null,
      }
    : null;

  // A scan having ever completed means results exist for this lot; once
  // completed, results remain available on subsequent (re-triggered) scans too.
  const hasPreviousResults = status?.isCompleted ?? false;

  const { trigger, isMutating } = useSWRMutation<void, SWRApiError, string, void>(
    `/pholis/scan/${measurementRequestId}`,
    async (url) => {
      await mutater(url, { arg: undefined });

      // mark the scan as enqueued in the cache; the polling will eventually
      // resolve to the real status once Pholis reports STARTED/COMPLETED/FAILED
      await refetchStatus({ is_running: false, runtime_status: "ENQUEUED" }, { revalidate: false });
    },
  );

  return {
    trigger,
    isMutating,
    status,
    hasPreviousResults,
    isLoadingStatus,
    refetchStatus,
  };
};
